import { useRef, useState } from 'react';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';

/** 응답 본문에서 보여줄 오류 메시지. 문자열로 내려온 값만 쓴다. */
const errorMessageOf = (error: unknown) => {
  const data = (
    error as { response?: { data?: { error?: unknown; message?: unknown } } }
  )?.response?.data;
  return [data?.error, data?.message].find(
    (message): message is string => typeof message === 'string',
  );
};

/**
 * 영역(카드) 하나를 저장할 때의 공통 처리.
 * - 저장 중 중복 요청 차단
 * - 변경 없음 / 성공 / 실패 알럿
 * - 저장을 마치면 수정 상태 종료
 *
 * 저장 요청과 변경 여부 판단은 영역마다 다르므로 `submit`에 넘겨받는다.
 */
export const useSectionSave = ({
  onClose,
  onSaved,
  successMessage = '수정이 완료되었습니다.',
  failMessage = '수정에 실패했습니다.',
}: {
  /** 저장을 마치고 수정 상태를 끝낸다. (변경된 내용이 없을 때도 포함) */
  onClose: () => void;
  /** 저장 성공 후 재조회. */
  onSaved: () => void;
  successMessage?: string;
  failMessage?: string;
}) => {
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);
  /** 저장 중복 요청 방지. 화면 갱신을 기다리지 않고 즉시 막아야 하므로 state와 별도로 둔다. */
  const savingRef = useRef(false);

  /** @param save 저장 요청. 바뀐 값이 없어 요청하지 않았으면 false를 돌려준다. */
  const submit = async (save: () => Promise<boolean>) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);

    try {
      const changed = await save();
      if (!changed) {
        dispatch(openAlert({ message: '변경된 내용이 없습니다.' }));
        onClose();
        return;
      }

      onSaved();
      dispatch(openAlert({ message: successMessage }));
      onClose();
    } catch (err) {
      console.error(err);
      // 네트워크/500 에러는 인터셉터가 이미 알럿을 띄운다. (__handled)
      if ((err as { __handled?: boolean })?.__handled) return;
      dispatch(openAlert({ message: errorMessageOf(err) ?? failMessage }));
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return { isSaving, submit };
};
